import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { type EventCarousel } from "../lib/api/useEventsList";
import { EventCard as EventCardComponent } from "./EventCard";

// Horizontally-scrolling rail of event cards. Handles drag-to-scroll, desktop
// arrow buttons, scroll/position persistence, and lazy card mounting. Shared by
// the events index and any other surface that wants the same rail behavior
// (e.g. "Similar events" on an event page).

// Render this many cards up front, then append more as the rail is scrolled.
// Cards mount lazily so off-screen events don't pay for hooks/images until needed.
const RAIL_INITIAL_VISIBLE = 6;
const RAIL_LOAD_INCREMENT = 6;

export function CarouselRail({
  carousel,
  compact = false,
  accessory,
}: {
  carousel: EventCarousel;
  compact?: boolean;
  accessory?: ReactNode;
}) {
  const { title, items, largeCards } = carousel;
  // `compact` rails (everything below the kikis) use Partiful-sized (~192px) cards.
  const cardWidth = compact
    ? "w-[46%] sm:w-[210px] lg:w-[236px]"
    : largeCards
      ? "w-[85%] sm:w-[360px] lg:w-[420px]"
      : "w-[58%] sm:w-[240px] lg:w-[280px]";

  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartX = useRef(0);
  const dragScrollLeft = useRef(0);
  const hasDragged = useRef(false);

  const scrollKey = `rail-scroll-${carousel.id}`;
  const countKey = `rail-count-${carousel.id}`;

  // How many cards are actually rendered. Restored from the last visit so that
  // saved scroll position has enough content to scroll back to.
  const [visibleCount, setVisibleCount] = useState(() => {
    const saved = sessionStorage.getItem(countKey);
    const restored = saved ? parseInt(saved, 10) : 0;
    return Math.min(items.length, Math.max(RAIL_INITIAL_VISIBLE, restored || 0));
  });
  const visibleCountRef = useRef(visibleCount);
  visibleCountRef.current = visibleCount;
  const visibleItems = items.slice(0, visibleCount);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const saved = sessionStorage.getItem(scrollKey);
    if (saved) el.scrollLeft = parseInt(saved, 10);
    return () => {
      sessionStorage.setItem(scrollKey, String(el.scrollLeft));
      sessionStorage.setItem(countKey, String(visibleCountRef.current));
    };
  }, [scrollKey, countKey]);

  // Append more cards as the trailing sentinel scrolls into view. rootMargin
  // preloads the next batch before the user hits the end of the rail.
  useEffect(() => {
    if (visibleCount >= items.length) return;
    const root = scrollRef.current;
    const sentinel = sentinelRef.current;
    if (!root || !sentinel) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisibleCount((c) => Math.min(items.length, c + RAIL_LOAD_INCREMENT));
        }
      },
      { root, rootMargin: "0px 600px 0px 0px" },
    );
    io.observe(sentinel);
    return () => io.disconnect();
  }, [visibleCount, items.length]);

  // Track whether the rail has anything to scroll to in either direction so
  // we can hide arrows when they wouldn't do anything.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => {
      setCanLeft(el.scrollLeft > 4);
      setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", update);
      ro.disconnect();
    };
  }, [visibleCount]);

  // Advance by ~one visible page (like Partiful's rail) instead of a fixed
  // nudge, leaving a sliver of the current card for continuity. No scroll-snap —
  // matches Partiful, and the per-call `smooth` keeps the drag handler instant.
  const scrollByPage = (dir: 1 | -1) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.round(el.clientWidth * 0.9), behavior: "smooth" });
  };

  const onMouseDown = (e: React.MouseEvent) => {
    const el = scrollRef.current;
    if (!el) return;
    e.preventDefault();
    hasDragged.current = false;
    dragStartX.current = e.pageX - el.getBoundingClientRect().left;
    dragScrollLeft.current = el.scrollLeft;
    setIsDragging(true);

    // Register listeners synchronously so they catch every mousemove/mouseup,
    // even ones that fire before the next React render (useEffect is too late).
    const onMove = (ev: MouseEvent) => {
      const x = ev.pageX - el.getBoundingClientRect().left;
      const delta = x - dragStartX.current;
      if (Math.abs(delta) > 3) hasDragged.current = true;
      el.scrollLeft = dragScrollLeft.current - delta;
    };
    const onUp = () => {
      setIsDragging(false);
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  const onClickCapture = (e: React.MouseEvent) => {
    if (hasDragged.current) {
      e.stopPropagation();
      e.preventDefault();
    }
  };

  // Large rails with only 1–2 items underfill a wide scroll rail and leave dead
  // space — render them as a centered, filled grid instead (no scroll/arrows).
  if (largeCards && !compact && items.length > 0 && items.length <= 2) {
    const cols = items.length;
    return (
      <section>
        <div className="w-full sm:w-[80vw] max-w-[1504px] mx-auto px-4 sm:px-6 mb-4">
          <h2 className="text-2xl sm:text-4xl font-bold tracking-tight">
          {title}
        </h2>
        </div>
        <div className="w-full sm:w-[80vw] max-w-[1504px] mx-auto px-4 sm:px-6">
          <div
            className="grid gap-4 sm:gap-5"
            style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
          >
            {items.map((event, i) => (
              <EventCardComponent
                key={event.link}
                event={event}
                index={i}
                largeCards={largeCards}
              />
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section>
      <div className="w-full sm:w-[80vw] max-w-[1504px] mx-auto px-4 sm:px-6 flex items-end justify-between gap-3 mb-4">
        <h2 className="text-2xl sm:text-4xl font-bold tracking-tight flex items-center gap-2">
          {title}
          {accessory}
        </h2>
        <div className="hidden sm:flex items-center gap-1">
          <button
            type="button"
            onClick={() => scrollByPage(-1)}
            disabled={!canLeft}
            aria-label={`Scroll ${title} left`}
            className="p-1.5 rounded-full bg-base-200 hover:bg-base-300 disabled:opacity-40 disabled:cursor-default transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => scrollByPage(1)}
            disabled={!canRight}
            aria-label={`Scroll ${title} right`}
            className="p-1.5 rounded-full bg-base-200 hover:bg-base-300 disabled:opacity-40 disabled:cursor-default transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto px-4 sm:px-6 pb-2 -mb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        style={{ cursor: isDragging ? "grabbing" : "grab" }}
        onMouseDown={onMouseDown}
        onClickCapture={onClickCapture}
      >
        {visibleItems.map((event, i) => (
          <div
            key={event.link}
            className={`shrink-0 ${cardWidth}`}
          >
            <EventCardComponent
              event={event}
              index={i}
              largeCards={largeCards && !compact}
            />
          </div>
        ))}
        {visibleCount < items.length && (
          <div ref={sentinelRef} aria-hidden className="shrink-0 w-px" />
        )}
      </div>
    </section>
  );
}
